import { View } from 'react-native';
import TextLato from '../../../../text/text-lato/TextLato';
import { useEffect, useState } from 'react';
import { useStore } from 'src/contexts/StoreContext';
import { styleDesktop, styleMobile } from './PickerGooglePhoto.style';
import {
  pollSession,
  getMediaItems,
  getImageFromMediaItem,
  startSession,
  deleteAccessToken,
  checkScopes,
  getAccountInfo,
  readAccessToken,
  isAccessTokenValid,
} from 'src/helpers/google.helper';
import { observer } from 'mobx-react-lite';
import { PickerGooglePhotoLoadingContent } from './picker-google-photo-loading-content/PickerGooglePhotoLoadingContent';
import { BannerLoggedUser } from './BannerLoggedUser';
import { useSizeLimitContext } from 'src/contexts/SizeLimitContext';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { Icon } from '@photosi/rn-components/icon/Icon';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { SourcesEventType } from '../ModalUploadMultisource.constants';
import { SCOPE_GOOGLE_PHOTO } from 'src/constants/google.constants';
import { filesToPickedImages } from 'src/_shared/utils/filesToPickedImages';
import { openWindow } from 'src/_shared/utils/open-window/openWindow';
import { EDITOR_BASE_URL } from 'src/app/settings';
import { ROUTE_NAMES, ROUTE_PATHS } from 'src/routes/_shared/routes.constants';
import { useNavigation, useRoute } from '@react-navigation/native';
import { convertObjectToQueryString } from 'src/_shared/utils/convertObjectToQueryString';
import { SecondaryButton } from 'src/editor-v3/components/buttons/secondary-button/SecondaryButton';
import { TestIDs } from 'src/constants/testIDs.constants';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { SELECTED_MENU_ITEMS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { DynamicModalProps } from '../../DynamicModal.types';
import { usePolling } from 'src/hooks/usePolling';
import { logError, logWarning } from 'src/_shared/logger';
import { convertArrayBufferToFile } from 'src/helpers/image.helper';
import { PickerGooglePhotoErrors } from '@photosi/rn-components/picker-google-photo/picker-google-photo-errors/PickerGooglePhotoErrors';

export const PickerGooglePhotos = observer(
  ({
    dynamicModalLayer: { isUploadingPhoto, uploadMultiSourceCallback, setSelectedItem },
    selectedCard,
  }: DynamicModalProps) => {
    const {
      i18n,
      toastStore,
      photoDrawer,
      googleAccount: {
        email,
        profilePic,
        isUserLogged,
        accessToken,
        setIsUserLogged,
        setEmail,
        setProfilePic,
        setAccessToken,
        setHasGivenAccessToPhoto,
        hasGivenAccessToPhoto,
      },
      project: { activeProject },
      dynamicModal: { closeModal },
    } = useStore();

    const route = useRoute();
    const newRoute = useNavigation().getState()?.routes[0].name === ROUTE_NAMES.EditorNew;

    const screenSize = useSizeLimitContext();
    const { isDesktop } = useResponsiveScreen();

    const componentStyle = isDesktop ? styleDesktop : styleMobile;

    const [sessionId, setSessionId] = useState<string>('');
    const [mediaItemsSet, setMediaItemsSet] = useState<boolean>(false);
    const { startPolling: startPollingCheckLogin, stopPolling: stopPollingCheckLogin } = usePolling();
    const { startPolling: startPollingSession, stopPolling: stopPollingSession } = usePolling();

    const handleLogin = async () => {
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachClick, {
        source_type: SourcesEventType.GooglePhoto,
      });

      await openWindow(`${EDITOR_BASE_URL}/${ROUTE_PATHS.GOOGLE_LOGIN}?${convertObjectToQueryString(route.params)}`, {
        target: 'null',
        dimensions: {
          width: (screenSize.width * 3) / 10,
          height: (screenSize.height * 6) / 10,
          left: (screenSize.width * 4) / 10,
          top: (screenSize.height * 2) / 10,
        },
      });

      startPollingCheckLogin(async () => {
        const accessToken = readAccessToken();
        if (accessToken) {
          try {
            await getAccountInfo({ accessToken: accessToken }).then(res => {
              setProfilePic(res.picture);
              setEmail(res.email);
            });
            await checkScopes(accessToken).then(res => {
              const userHasGivenAccessToPhoto = res.scope.includes(SCOPE_GOOGLE_PHOTO);
              if (userHasGivenAccessToPhoto) {
                setHasGivenAccessToPhoto(true);
                amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachSuccess, {
                  source_type: SourcesEventType.GooglePhoto,
                });
              } else {
                setHasGivenAccessToPhoto(false);
                amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachError, {
                  source_type: SourcesEventType.GooglePhoto,
                  error: '',
                });
              }
            });
            setAccessToken(accessToken);
            setIsUserLogged(true);
            stopPollingCheckLogin();
          } catch (error) {
            logError(error);
          }
        }
        return void 0;
      });
    };

    const handleChoosePhoto = async () => {
      return startSession({ accessToken })
        .then(async res => {
          setSessionId(res.sessionId);
          startPollingSession(async () => {
            if (!mediaItemsSet) {
              try {
                if (!isAccessTokenValid()) {
                  stopPollingSession();
                  handleLogout();
                  return;
                }
                const newMediaItemsSet = await pollSession({ sessionId: res.sessionId, accessToken });
                if (newMediaItemsSet) {
                  setMediaItemsSet(newMediaItemsSet);
                  stopPollingSession();
                }
              } catch (error) {
                logError(error);
              }
            }
          }, res.interval);

          await openWindow(
            res.pickerUri,
            isDesktop
              ? {
                  target: 'null',
                  dimensions: {
                    width: (screenSize.width * 6) / 10,
                    height: (screenSize.height * 6) / 10,
                    left: (screenSize.width * 2) / 10,
                    top: (screenSize.height * 2) / 10,
                  },
                }
              : undefined,
          );
        })
        .catch(error => {
          logWarning('[Google Photo] - Choose Photo Error: ', error);
          setHasGivenAccessToPhoto(false);
        });
    };

    const handleLogout = () => {
      deleteAccessToken();
      setProfilePic('');
      setEmail('');
      setIsUserLogged(false);
      amplitude.trackEvent(AnalyticsEvents.photoSources.detachSourceClick, { source: SourcesEventType.GooglePhoto });
    };

    useEffect(() => {
      if (mediaItemsSet) {
        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick, {
          source_type: SourcesEventType.GooglePhoto,
        });
        getMediaItems({ sessionId, accessToken }).then(async res => {
          // @ts-ignore
          const googlePhotos: GooglePhotoItem[] = res.filter(item => item.type === 'PHOTO');
          let pickedImages = [];
          if (newRoute) {
            pickedImages = await filesToPickedImages(
              googlePhotos,
              {
                i18n,
                errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
                  if (areAllPhotosInvalid) {
                    closeModal();
                  }

                  toastStore.showErrorToast({
                    id: 'alert-uploading-photos-error-toast',
                    message: errorMessage,
                  });
                },
              },
              newRoute,
            );
            const autoselect = activeProject!.thumbnailImages.length == 0;
            pickedImages.forEach(pickedImage => (pickedImage.source = SELECTED_MENU_ITEMS.GooglePhoto));
            await activeProject?.addImages(pickedImages, accessToken).then(() => {
              if (autoselect) {
                activeProject.thumbnailImages.forEach(image => {
                  photoDrawer.addImageId(image.originalImageId);
                });
              }
              isDesktop && setSelectedItem(SELECTED_MENU_ITEMS.Computer);
              !isDesktop && setSelectedItem(SELECTED_MENU_ITEMS.Gallery);
            });
          } else {
            Promise.all(
              // @ts-ignore
              googlePhotos.map(item => {
                return getImageFromMediaItem({ item, accessToken }).then(async res => {
                  return convertArrayBufferToFile({
                    arrayBuffer: res.data,
                    fileName: item.mediaFile.filename,
                    lastModified: new Date(item.createTime).getTime(),
                    mimeType: 'image/jpeg', // all google images fetched are in jpeg format
                  });
                });
              }),
            ).then(async files => {
              pickedImages = await filesToPickedImages(
                files,
                {
                  i18n,
                  errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
                    if (areAllPhotosInvalid) {
                      closeModal();
                    }
                    toastStore.showErrorToast({
                      id: 'alert-uploading-photos-error-toast',
                      message: errorMessage,
                    });
                  },
                },
                newRoute,
              );
              uploadMultiSourceCallback({ files: pickedImages });
            });
          }

          amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
            n_photo: pickedImages.length,
            source_type: SourcesEventType.GooglePhoto,
          });
        });
      }
    }, [mediaItemsSet]);

    return (
      <View style={componentStyle.container}>
        {isUploadingPhoto ? (
          <PickerGooglePhotoLoadingContent />
        ) : (
          <View style={componentStyle.modalContainer}>
            <View style={componentStyle.wrapperContainer}>
              <View style={[componentStyle.content, !isDesktop && !isUserLogged && { gap: 40 }]}>
                <View style={componentStyle.googlePhotoIcon}>
                  <Icon name={ICON_NAMES.GooglePhoto} />
                </View>
                <View style={componentStyle.text}>
                  <TextLato style={componentStyle.title}>
                    {isUserLogged
                      ? i18n.t('modal-picker-google-photo:title-logged')
                      : i18n.t('modal-picker-google-photo:title-upload')}
                  </TextLato>
                  <TextLato style={componentStyle.description}>
                    {isUserLogged
                      ? i18n.t('modal-picker-google-photo:description-logged')
                      : i18n.t('modal-picker-google-photo:description-upload')}
                  </TextLato>
                </View>
                {isUserLogged && (
                  <View>
                    <PrimaryButton
                      style={[
                        componentStyle.buttonPrimary,
                        !hasGivenAccessToPhoto && componentStyle.buttonPrimaryDisabled,
                      ]}
                      onPress={handleChoosePhoto}
                      disabled={!hasGivenAccessToPhoto}
                      text={i18n.t(!selectedCard ? 'modal-picker-google-photo:choose-photo' : 'upload:choose-max', {
                        photos: selectedCard?.replace('x', ''),
                      })}
                      textStyle={[
                        componentStyle.buttonPrimaryText,
                        !hasGivenAccessToPhoto && componentStyle.buttonPrimaryTextDisabled,
                      ]}
                      testID={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.CHOOSE_GOOGLE_PHOTO)}
                      focusId={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.CHOOSE_GOOGLE_PHOTO)}
                      focusOrder={0}
                      focusGroup={FocusGroups.MODAL}
                      size={BUTTON_SIZES.REGULAR}
                      type={BUTTON_TYPES.DEFAULT}
                      layout={BUTTON_LAYOUTS.FILL}
                    />
                  </View>
                )}
                {!isUserLogged && (
                  <View style={{ paddingTop: 16 }}>
                    <SecondaryButton
                      style={componentStyle.buttonSecondary}
                      onPress={handleLogin}
                      testID={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.GOOGLE_PHOTO_LOGIN)}
                      focusId={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.GOOGLE_PHOTO_LOGIN)}
                      focusOrder={0}
                      focusGroup={FocusGroups.MODAL}
                      text={i18n.t('modal-picker-google-photo:login')}
                      textStyle={componentStyle.buttonSecondaryText}
                      iconName={ICON_NAMES.GooglePhoto}
                      iconStyle={componentStyle.googlePhotoIconSmall}
                      size={BUTTON_SIZES.REGULAR}
                      type={BUTTON_TYPES.ICON_LEFT}
                      layout={BUTTON_LAYOUTS.HUG}
                      accessible={true}
                      accessibilityLabel={i18n.t('modal-picker-google-photo:login')}
                      accessibilityRole="button"
                    />
                  </View>
                )}
              </View>
              <PickerGooglePhotoErrors />
            </View>
            {isUserLogged && !!email && (
              <View style={{ paddingTop: 16 }}>
                <BannerLoggedUser profilePic={profilePic} email={email} handleLogout={handleLogout} />
              </View>
            )}
          </View>
        )}
      </View>
    );
  },
);
